'use client';

import React from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';

export interface ProviderPopupMetric {
  key: string;
  label: string;
  value: string;
}

export interface ProviderPopupDetailsProps {
  name?: string;
  specialty?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  /** Profile URL; renders the name as a link. Takes precedence over `onNameClick`. */
  href?: string;
  /** Click handler; renders the name as a button when there is no `href`. */
  onNameClick?: () => void;
  /** Id for the name element, e.g. to label a checkbox. */
  nameId?: string;
  nameTestId?: string;
  /** Heading above the metric rows, e.g. the volume being shown. */
  metricLabel?: string;
  /** Label-and-value rows, already formatted. */
  metrics?: ProviderPopupMetric[];
}

const NAME_CLASS_NAME =
  'group/link inline-flex items-start gap-2 text-sm font-medium text-sky-600 no-underline hover:text-sky-900 hover:underline text-left';

/**
 * The provider summary shown in map marker popups: linked name, specialty, address
 * and an optional block of metric rows. Shared by the Strategy and Discovery maps so
 * their popups look alike.
 */
const ProviderPopupDetails = ({
  name,
  specialty,
  addressLine1,
  addressLine2,
  city,
  state,
  href,
  onNameClick,
  nameId,
  nameTestId,
  metricLabel,
  metrics,
}: ProviderPopupDetailsProps) => {
  const nameContent = (
    <>
      {name || 'Provider'}
      <Icon
        icon={faExternalLinkAlt}
        className="h-3 w-3 text-sky-600 group-hover/link:text-sky-800"
      />
    </>
  );

  return (
    <div className="flex flex-col gap-1">
      {href ? (
        <Link
          id={nameId}
          href={href}
          scroll={false}
          className={NAME_CLASS_NAME}
          data-testid={nameTestId}
        >
          {nameContent}
        </Link>
      ) : (
        <button
          type="button"
          id={nameId}
          onClick={onNameClick}
          className={NAME_CLASS_NAME}
          data-testid={nameTestId}
        >
          {nameContent}
        </button>
      )}
      <div className="flex flex-col">
        {specialty && (
          <p className="text-xs text-neutral-600 font-bold">{specialty}</p>
        )}
        {(addressLine1 || city || state) && (
          <p className="text-xs text-neutral-500">
            {addressLine1 || ''}
            {addressLine1 && (city || state) ? ', ' : ''}
            {city}
            {city && state ? ', ' : ''}
            {state}
          </p>
        )}
        {addressLine2 && (
          <p className="text-xs text-neutral-500">{addressLine2}</p>
        )}
      </div>
      {!!metrics?.length && (
        <div
          className="flex flex-col gap-1 pt-2 border-t border-neutral-100"
          data-testid="provider-popup-metrics"
        >
          {metricLabel && (
            <span
              className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500"
              data-testid="provider-popup-metric-label"
            >
              {metricLabel}
            </span>
          )}
          {metrics.map((metric) => (
            <div
              key={metric.key}
              className="flex items-baseline justify-between gap-3"
              data-testid={`provider-popup-metric-${metric.key}`}
            >
              <span className="text-xs text-neutral-600">{metric.label}</span>
              <span className="text-sm font-bold text-neutral-800">
                {metric.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProviderPopupDetails;
